<template>
  <div>
    <!-- Search trigger button -->
    <button @click="openSearch" class="p-2 text-2xl">
      <v-icon name="fa-search" class="w-6 h-6"></v-icon>
    </button>

    <!-- Full screen overlay with transition -->
    <transition name="fade">
      <div v-if="showSearch" class="search-overlay" @click="handleOverlayClick">
        <transition name="slide-down">
          <div v-if="showSearch" class="search-container" ref="searchContainer">
            <div class="search-header">
              <h2 class="search-title">Search Products</h2>
              <button @click="closeSearch" class="close-button">
                <v-icon name="la-times-solid"></v-icon>
              </button>
            </div>
            
            <div class="search-input-container">
              <div class="search-input-wrapper">
                <v-icon name="fa-search" class="search-icon"></v-icon>
                <input 
                  type="text" 
                  v-model="value" 
                  @input="onSearch"
                  placeholder="Search for products..."
                  class="search-input"
                  ref="searchInput"
                  autofocus
                />
                <button v-if="value" @click="clearSearch" class="clear-button">
                  <v-icon name="la-times-circle" class="clear-icon"></v-icon>
                </button>
              </div>
            </div>

            <div class="search-results-container">
              <div v-if="loading" class="search-loading">
                <div class="spinner"></div>
                <span>Searching...</span>
              </div>
              
              <div v-else-if="results.length > 0" class="search-results">
                <div 
                  v-for="(item, index) in results" 
                  :key="index" 
                  class="search-result-item"
                  @click="selectItem(item)"
                >
                  <div class="search-result-image">
                    <img :src="item.image_url || '/images/placeholder.jpg'" :alt="item.shopfront_name">
                  </div>
                  <div class="search-result-content">
                    <h3 class="search-result-title">{{ item.shopfront_name }}</h3>
                    <p class="search-result-description">{{ item.shopfront_description }}</p>
                    <div class="search-result-meta">
                      <span v-if="item.price" class="search-result-price">{{ formatPrice(item.price) }}</span>
                      <span v-if="item.category" class="search-result-category">{{ item.category }}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-else-if="value && !loading" class="no-results">
                <v-icon name="la-search" class="no-results-icon"></v-icon>
                <p>No results found for "<strong>{{ value }}</strong>"</p>
                <p class="no-results-suggestion">Try different keywords or check your spelling</p>
              </div>
              
              <div v-else-if="!value && !loading" class="search-prompt">
                <v-icon name="la-search" class="search-prompt-icon"></v-icon>
                <p>Start typing to search for products</p>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>
  
<script lang="ts">
import Axios from "axios";
import { defineComponent, ref, onMounted, onUnmounted } from "vue";
  
export default defineComponent({
  components: {
  },
  data() {
    return {
      searchTimeout: null,
    };
  },
  setup() {
    const results = ref<any[]>([]);
    const value = ref("");
    const option = ref<any>(null);
    const showSearch = ref(false);
    const loading = ref(false);
    const searchInput = ref<HTMLInputElement | null>(null);
    const searchContainer = ref<HTMLElement | null>(null);
    
    // Handle escape key to close search
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showSearch.value) {
        showSearch.value = false;
      }
    };
    
    onMounted(() => {
      document.addEventListener('keydown', handleEscKey);
    });
    
    onUnmounted(() => {
      document.removeEventListener('keydown', handleEscKey);
    });
    
    return {
      results,
      value,
      option,
      showSearch,
      loading,
      searchInput,
      searchContainer
    };
  },
  methods: {
    openSearch: function () {
      this.showSearch = true;
      this.value = "";
      this.results = [];
      
      // Focus the input after the DOM updates
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          (this.$refs.searchInput as HTMLInputElement).focus();
        }
      });
    },
    
    closeSearch: function () {
      this.showSearch = false;
    },
    
    clearSearch: function() {
      this.value = "";
      this.results = [];
      if (this.$refs.searchInput) {
        (this.$refs.searchInput as HTMLInputElement).focus();
      }
    },
    
    handleOverlayClick: function (event) {
      // Close search when clicking outside the search container
      if (this.$refs.searchContainer && 
          !(this.$refs.searchContainer as HTMLElement).contains(event.target as Node)) {
        this.closeSearch();
      }
    },
    
    onSearch: async function () {
      // Debounce search to avoid too many requests
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      if (!this.value) {
        this.results = [];
        return;
      }
      
      this.loading = true;
      this.searchTimeout = setTimeout(async () => {
        await this.search({ query: this.value });
        this.loading = false;
      }, 300) as any;
    },
    
    search: async function (event: any) {
      try {
        const result = await Axios.get(
          `/api/products/autocomplete/${event.query}`
        );
        this.results = result.data;
      } catch (error) {
        console.error("Search error:", error);
        this.results = [];
      }
    },
    
    selectItem: function (item: any) {
      // Navigate to product page
      window.location.href = item.product_url;
      this.closeSearch();
    },
    
    formatPrice: function(price: number | string) {
      if (!price) return '';
      
      // Convert to number if it's a string
      const numPrice = typeof price === 'string' ? parseFloat(price) : price;
      
      // Format with currency symbol
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(numPrice);
    }
  }
});
</script>

<style scoped>
.search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 5vh;
  overflow-y: auto;
  backdrop-filter: blur(3px);
}

.search-container {
  width: 90%;
  max-width: 800px;
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
}

.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
}

.search-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: #f5f5f5;
  color: #333;
}

.search-input-container {
  margin-bottom: 1.5rem;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  border: 2px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.search-input-wrapper:focus-within {
  border-color: var(--primary-color, #4a90e2);
  box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
}

.search-icon {
  position: absolute;
  left: 1rem;
  color: #999;
  width: 1.2rem;
  height: 1.2rem;
}

.search-input {
  width: 100%;
  padding: 1rem 3rem;
  font-size: 1.1rem;
  border: none;
  outline: none;
  background: transparent;
}

.clear-button {
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.3rem;
  border-radius: 50%;
}

.clear-button:hover {
  color: #666;
  background-color: #f5f5f5;
}

.clear-icon {
  width: 1.2rem;
  height: 1.2rem;
}

.search-results-container {
  position: relative;
  min-height: 200px;
}

.search-results {
  max-height: 60vh;
  overflow-y: auto;
  border-radius: 8px;
}

.search-result-item {
  display: flex;
  padding: 1.2rem;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 8px;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-item:hover {
  background-color: #f9f9f9;
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}

.search-result-image {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
  margin-right: 1.2rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.search-result-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.search-result-item:hover .search-result-image img {
  transform: scale(1.05);
}

.search-result-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.search-result-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
}

.search-result-description {
  margin: 0 0 0.8rem 0;
  font-size: 0.95rem;
  color: #666;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}

.search-result-meta {
  display: flex;
  align-items: center;
  margin-top: auto;
}

.search-result-price {
  font-weight: 600;
  color: var(--primary-color, #4a90e2);
  margin-right: 1rem;
}

.search-result-category {
  font-size: 0.85rem;
  color: #888;
  background-color: #f5f5f5;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
}

.no-results {
  padding: 3rem 1rem;
  text-align: center;
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.no-results-icon {
  font-size: 3rem;
  color: #ddd;
  margin-bottom: 1rem;
}

.no-results p {
  margin: 0.5rem 0;
  font-size: 1.1rem;
}

.no-results-suggestion {
  color: #999;
  font-size: 0.9rem !important;
}

.search-prompt {
  padding: 3rem 1rem;
  text-align: center;
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.search-prompt-icon {
  font-size: 3rem;
  color: #ddd;
  margin-bottom: 1rem;
}

.search-prompt p {
  margin: 0.5rem 0;
  font-size: 1.1rem;
  color: #999;
}

.search-loading {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: #666;
  height: 200px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: var(--primary-color, #4a90e2);
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Animation styles */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.4s ease, opacity 0.4s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-30px);
  opacity: 0;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .search-container {
    width: 95%;
    padding: 1rem;
  }
  
  .search-result-image {
    width: 80px;
    height: 80px;
  }
  
  .search-result-title {
    font-size: 1.1rem;
  }
  
  .search-result-description {
    font-size: 0.9rem;
    -webkit-line-clamp: 1;
  }
}
</style>
  